import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { ActionCard } from "@ozpanel/kit";
import { Panel, Section, StateView, useAuth } from "@ozpanel/ui";

/**
 * The replication cluster as repmgr sees it, and the repair for the one state that used to be
 * invisible.
 *
 * This module reported "healthy" for a week while PostgreSQL ran as TWO independent primaries on
 * divergent timelines with no replication between them. Its health check was written OK: true
 * unconditionally and looked only at a systemd unit; its status endpoint published the --csv
 * columns as {id, role_code, status_code}, which are not a role and not a status — they are the
 * node id, whether it is reachable, and its upstream id. The console rendered each node as
 * "[object Object]", so even the meaningless numbers were not visible.
 *
 * A failover daemon that reports only on itself cannot report the one thing it exists for. What is
 * shown here is the cluster: who is primary, who follows whom, on which timeline.
 */

type Node = {
  id: string;
  name: string;
  role: string;
  status: string;
  upstream: string;
  timeline: string;
  reachable: boolean;
  running_as_primary: boolean;
};

type Status = {
  active: boolean;
  nodes?: Node[] | null;
  primaries?: number;
  diverged?: string[] | null;
  unreachable?: string[] | null;
  timelines?: string[] | null;
};

export function RepmgrdOps() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["repmgrd-status"],
    queryFn: () => client.apiFetch<Status>("/repmgrd/status"),
    refetchInterval: 30000,
  });
  const d = q.data;
  const nodes = d?.nodes ?? [];
  const split = (d?.primaries ?? 0) > 1;
  const diverged = d?.diverged ?? [];

  const cols: DataListColumn<Node>[] = [
    { key: "name", label: "Node", sortable: true },
    {
      key: "role",
      label: "Registered role",
      sortable: true,
      render: (n) => <StatusPill tone={n.role === "primary" ? "accent" : "neutral"}>{n.role}</StatusPill>,
    },
    {
      key: "status",
      label: "Actually doing",
      // The whole point: the REGISTERED role and what the node is DOING are different facts, and
      // the gap between them is the fault. A standby "running as primary" is a split brain.
      render: (n) =>
        !n.reachable ? (
          <StatusPill tone="bad">{n.status || "unreachable"}</StatusPill>
        ) : n.role === "standby" && n.running_as_primary ? (
          <StatusPill tone="bad">running as primary — diverged</StatusPill>
        ) : (
          <span className="text-[12.5px]">{n.status}</span>
        ),
    },
    { key: "upstream", label: "Follows", render: (n) => <span className="font-mono text-[12px]">{n.upstream || "—"}</span> },
    {
      key: "timeline",
      label: "Timeline",
      // Two timelines in one cluster means the histories have forked. Coloured, because it is the
      // difference between "behind" and "a different database".
      render: (n) => (
        <StatusPill tone={(d?.timelines?.length ?? 1) > 1 ? "bad" : "neutral"}>{n.timeline}</StatusPill>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {split && (
        <Panel className="border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-3 text-[12.5px] leading-relaxed">
          <b>Split brain: {d?.primaries} nodes are running as PostgreSQL primaries.</b> Two independent
          write targets are accepting writes, neither replicates to the other, and nothing else on the
          node reports an error.
          {diverged.length > 0 && (
            <>
              {" "}
              <span className="font-mono">{diverged.join(", ")}</span> diverged from the registered
              primary
              {(d?.timelines?.length ?? 0) > 1 && <> on timeline {d?.timelines?.join(" and ")}</>}.
            </>
          )}{" "}
          Repair it by rejoining the node that should become the standby — <em>on that node</em>.
        </Panel>
      )}

      <Section title="Replication cluster" hint={d ? `${nodes.length} node(s)` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <StateView loading={q.isLoading} error={q.error} errorContext="the replication cluster">
            <DataList
              items={nodes}
              columns={cols}
              getRowKey={(n) => n.id}
              emptyMessage="repmgr reports no cluster on this node."
              emptyHint="Expected on a single-node install — and NOT a statement that replication is healthy."
            />
          </StateView>
        </div>
      </Section>

      <Section title="Recovery">
        <ActionCard
          title="Rejoin this node as a standby"
          description="Rewinds this node onto the registered primary's timeline and follows it. This is the repair for a node that diverged and is running as a second primary."
          actionLabel="Rejoin"
          danger="high"
          // Offered only when this node is the one that should be repaired. On the primary it
          // would rewind the cluster's own history onto another node's timeline, and the server
          // refuses it — but an action that can only be refused should not be presented.
          unavailable={
            !d
              ? "The cluster state has not been read yet."
              : !split
                ? "No node is running as a second primary, so there is nothing to rejoin. Replication is either healthy or not configured."
                : undefined
          }
          confirm={{
            title: "Rejoin this node to the primary?",
            body: "Everything written to THIS node since the timelines diverged is discarded. The other node's history becomes the cluster's history. Take a copy of this node's data directory first if there is any doubt.",
            confirmLabel: "Rewind and rejoin",
          }}
          run={() => client.apiFetch("/repmgrd/rejoin", { method: "POST" })}
          invalidates={[["repmgrd-status"], ["pg-cluster"]]}
        />
      </Section>
    </div>
  );
}
